import { useState, useMemo, useCallback, useEffect } from "react";
import {
  useGetList,
  useTranslate,
  useRedirect,
} from "react-admin";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Chip,
  Avatar,
  useTheme,
  Paper,
  Autocomplete,
  TextField,
  Button,
  Tooltip,
  Link,
  Divider,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import { formatPhoneForDisplay } from "../../utils/phone";
import type { JobApplication } from "./JobApplicationCard";

const getWhatsAppLink = (phone: string): string => {
  const cleanPhone = phone.replace(/[^\d+]/g, "");
  const phoneForLink = cleanPhone.startsWith("+")
    ? cleanPhone
    : `+${cleanPhone}`;
  return `https://wa.me/${phoneForLink.replace(/\+/g, "")}`;
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

function formatDateBR(date: Date): string {
  return date.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// Print-only stylesheet: hides React Admin shell (sidebar, app bar) so only
// the calendar content is printed. Injected on mount, removed on unmount.
const PRINT_STYLE_ID = "calendar-print-style";
const PRINT_CSS = `
@media print {
  /* Hide RA sidebar / menu */
  .RaLayout-sidebar,
  .RaSidebar-root,
  .MuiDrawer-root,
  nav[class*="Menu"] {
    display: none !important;
  }
  /* Hide RA app bar */
  .RaLayout-appBar,
  .MuiAppBar-root,
  header {
    display: none !important;
  }
  /* Make content area full-width */
  .RaLayout-content,
  .RaLayout-contentWithSidebar,
  main {
    margin-left: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  body {
    overflow: visible !important;
  }
  /* Hide MUI tooltips/poppers (rendered at body level via portal) */
  .MuiTooltip-popper,
  .MuiPopper-root {
    display: none !important;
  }
}
`;

const NO_COMPANY_KEY = "__no_company__";

export const JobApplicationCalendar = () => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const theme = useTheme();

  const calendarTitle = translate("resources.job-application-calendar.title");

  // Set document.title manually
  useEffect(() => {
    const prev = document.title;
    document.title = calendarTitle;
    return () => {
      document.title = prev;
    };
  }, [calendarTitle]);

  // Inject print stylesheet
  useEffect(() => {
    const style = document.createElement("style");
    style.id = PRINT_STYLE_ID;
    style.textContent = PRINT_CSS;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [companyFilter, setCompanyFilter] = useState<number | null>(null);

  // Date range for the selected day
  const dateRange = useMemo(() => {
    const start = new Date(selectedDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(selectedDate);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [selectedDate]);

  // Build filter for the API
  const filter = useMemo(() => {
    const f: Record<string, unknown> = {
      interviewDate_gte: dateRange.start.toISOString(),
      interviewDate_lte: dateRange.end.toISOString(),
    };
    if (companyFilter) f.companyId_eq = companyFilter;
    return f;
  }, [dateRange, companyFilter]);

  const { data: applications = [], isLoading } = useGetList<JobApplication>(
    "job-application",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "interviewDate", order: "ASC" },
      filter,
    }
  );

  const { data: companies = [] } = useGetList("company", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });

  const { data: statuses = [] } = useGetList("job-application-status", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "order", order: "ASC" },
  });

  const companyMap = useMemo(() => {
    const m: Record<number, string> = {};
    companies.forEach((c: { id: number; name: string }) => {
      m[c.id] = c.name;
    });
    return m;
  }, [companies]);

  const statusMap = useMemo(() => {
    const m: Record<number, string> = {};
    statuses.forEach((s: { id: number; name: string }) => {
      m[s.id] = s.name;
    });
    return m;
  }, [statuses]);

  // Group applications by company, sorted by time within each group
  const groupedByCompany = useMemo(() => {
    const filtered = applications.filter((app) => !!app.interviewDate);
    const groups: Record<string, { companyName: string; apps: JobApplication[] }> = {};

    filtered.forEach((app) => {
      const key = app.companyId ? String(app.companyId) : NO_COMPANY_KEY;
      if (!groups[key]) {
        groups[key] = {
          companyName: app.companyId && companyMap[app.companyId]
            ? companyMap[app.companyId]
            : translate("resources.job-application-calendar.noCompany"),
          apps: [],
        };
      }
      groups[key].apps.push(app);
    });

    // Sort apps within each group by interview time
    Object.values(groups).forEach((g) =>
      g.apps.sort(
        (a, b) =>
          new Date(a.interviewDate!).getTime() -
          new Date(b.interviewDate!).getTime()
      )
    );

    // Sort groups: named companies alphabetically, "sem empresa" last
    return Object.entries(groups).sort(([keyA], [keyB]) => {
      if (keyA === NO_COMPANY_KEY) return 1;
      if (keyB === NO_COMPANY_KEY) return -1;
      return (groups[keyA].companyName).localeCompare(groups[keyB].companyName);
    });
  }, [applications, companyMap, translate]);

  const totalCount = useMemo(
    () => groupedByCompany.reduce((sum, [, g]) => sum + g.apps.length, 0),
    [groupedByCompany]
  );

  const hasMultipleCompanies = groupedByCompany.length > 1;

  const goToday = useCallback(() => setSelectedDate(new Date()), []);
  const goPrev = useCallback(() => {
    setSelectedDate((d) => {
      const n = new Date(d);
      n.setDate(n.getDate() - 1);
      return n;
    });
  }, []);
  const goNext = useCallback(() => {
    setSelectedDate((d) => {
      const n = new Date(d);
      n.setDate(n.getDate() + 1);
      return n;
    });
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const dateLabel = useMemo(() => formatDateBR(selectedDate), [selectedDate]);

  const renderInterviewCard = (app: JobApplication, showCompanyChip: boolean) => {
    const interviewTime = new Date(app.interviewDate!);
    const timeStr = interviewTime.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <Card
        key={String(app.id)}
        onClick={() => redirect(`/job-application/${app.id}`)}
        sx={{
          p: 2,
          mb: 1.5,
          cursor: "pointer",
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          borderRadius: 1.5,
          transition: "all 0.15s ease",
          "&:hover": {
            boxShadow: theme.shadows[4],
            borderLeftColor: theme.palette.primary.dark,
          },
          "@media print": {
            boxShadow: "none",
            border: `1px solid #ccc`,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            breakInside: "avoid",
          },
        }}
      >
        {/* Header: avatar + name + time */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            {getInitials(app.candidateName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              noWrap
              sx={{ fontSize: "0.9rem" }}
            >
              {app.candidateName}
            </Typography>
            <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
              {timeStr}
            </Typography>
          </Box>
        </Box>

        {/* Contact info */}
        {app.email && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <EmailIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.75rem" }}>
              {app.email}
            </Typography>
          </Box>
        )}

        {app.phone && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Link
              href={getWhatsAppLink(app.phone)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                textDecoration: "none",
                "&:hover": { color: "#25D366" },
              }}
            >
              {formatPhoneForDisplay(app.phone).national}
            </Link>
          </Box>
        )}

        {/* Chips: status + company (company only when not grouped) */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
          {app.statusId && statusMap[app.statusId] && (
            <Chip
              label={statusMap[app.statusId]}
              size="small"
              color="primary"
              variant="filled"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
          )}
          {showCompanyChip && app.companyId && companyMap[app.companyId] && (
            <Chip
              label={companyMap[app.companyId]}
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.7rem",
                height: 22,
                borderColor: theme.palette.divider,
              }}
            />
          )}
        </Box>
      </Card>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {translate("ra.message.loading")}
          </Typography>
        </Box>
      );
    }

    if (totalCount === 0) {
      return (
        <Box sx={{ p: 6, textAlign: "center" }}>
          <Typography color="text.disabled" variant="body1">
            {translate("resources.job-application-calendar.noInterviews")}
          </Typography>
        </Box>
      );
    }

    // If only one company (or filter applied), just show cards flat
    if (!hasMultipleCompanies) {
      return (
        <Box sx={{ p: 2, maxWidth: 700, mx: "auto" }}>
          {groupedByCompany.map(([, group]) =>
            group.apps.map((app) => renderInterviewCard(app, false))
          )}
        </Box>
      );
    }

    // Multiple companies: group by company with headers
    return (
      <Box sx={{ p: 2, maxWidth: 700, mx: "auto" }}>
        {groupedByCompany.map(([key, group], idx) => (
          <Box
            key={key}
            sx={{
              "@media print": {
                breakInside: "avoid-column",
              },
            }}
          >
            {idx > 0 && <Divider sx={{ my: 2 }} />}
            {/* Wrapper that keeps the company header + first card together on print */}
            <Box sx={{ "@media print": { breakInside: "avoid" } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: idx > 0 ? 1 : 0 }}>
                <BusinessIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  {group.companyName}
                </Typography>
                <Chip
                  label={group.apps.length}
                  size="small"
                  color="default"
                  sx={{ fontSize: "0.75rem", height: 20, ml: 0.5 }}
                />
              </Box>
              {/* First card stays with the header */}
              {group.apps.length > 0 && renderInterviewCard(group.apps[0], false)}
            </Box>
            {/* Remaining cards */}
            {group.apps.slice(1).map((app) => renderInterviewCard(app, false))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        "@media print": {
          p: 0,
          "& .no-print": { display: "none !important" },
        },
      }}
    >
      {/* Toolbar */}
      <Paper
        elevation={1}
        className="no-print"
        sx={{
          p: 2,
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={translate("resources.job-application-calendar.today")}>
            <IconButton onClick={goToday} size="small">
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={goPrev} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={goNext} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Typography
          variant="h6"
          sx={{
            flex: 1,
            textAlign: "center",
            textTransform: "capitalize",
            minWidth: 200,
            fontWeight: 600,
          }}
        >
          {dateLabel}
        </Typography>

        {/* Company filter */}
        <Autocomplete
          size="small"
          options={companies as Array<{ id: number; name: string }>}
          getOptionLabel={(option) => option.name}
          value={
            companyFilter
              ? (companies as Array<{ id: number; name: string }>).find(
                  (c) => c.id === companyFilter
                ) || null
              : null
          }
          onChange={(_, v) => setCompanyFilter(v?.id || null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate("resources.job-application.fields.companyId")}
              sx={{ minWidth: 180 }}
            />
          )}
          sx={{ minWidth: 180 }}
        />

        {/* Export PDF */}
        <Tooltip title={translate("resources.job-application-calendar.exportPdf")}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePrint}
            size="small"
          >
            PDF
          </Button>
        </Tooltip>
      </Paper>

      {/* Print header (only visible when printing) */}
      <Box
        sx={{
          display: "none",
          "@media print": {
            display: "block",
            mb: 2,
            textAlign: "center",
          },
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          {calendarTitle}
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ textTransform: "capitalize" }}
        >
          {dateLabel}
        </Typography>
        {companyFilter && companyMap[companyFilter] && (
          <Typography variant="body2" color="text.secondary">
            {translate("resources.job-application.fields.companyId")}:{" "}
            {companyMap[companyFilter]}
          </Typography>
        )}
      </Box>

      {/* Day content */}
      <Paper
        elevation={1}
        sx={{
          overflow: "auto",
          "@media print": {
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        {renderContent()}
      </Paper>

      {/* Summary */}
      <Paper elevation={0} sx={{ mt: 2, p: 2 }} className="no-print">
        <Typography variant="body2" color="text.secondary">
          {translate("resources.job-application-calendar.totalInterviews", {
            count: totalCount,
          })}
        </Typography>
      </Paper>
    </Box>
  );
};
